import { useNavigate } from 'react-router-dom';
import type { PackArtKey, PricedCartGroup, PricedCartLine } from '@chicare/shared';

import { cartStore, useCartLines, usePricedCart } from '@/lib/cart';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { PackBox } from '@/components/ui/PackBox';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';

/**
 * The cart.
 *
 * Grouped by pharmacy, because that grouping is real: one cart becomes several
 * sub-orders, several riders and several delivery fees. Showing it here means
 * the buyer learns it BEFORE they pay rather than being surprised by two
 * charges afterwards.
 */
export function CartScreen() {
  const navigate = useNavigate();
  const lines = useCartLines();
  const { user } = useSession();
  const { data: cart, isLoading, isError, refetch } = usePricedCart();

  if (lines.length === 0) {
    return (
      <>
        <AppBar back title="Cart" />
        <EmptyState
          illustration={<EmptyCartArt />}
          title="Nothing in your cart yet"
          body="Search for a medicine, or browse verified pharmacies near you."
          action={
            <>
              <Button full onClick={() => navigate('/search')}>
                Search medicines
              </Button>
              <Button full variant="grey" className="mt-2" onClick={() => navigate('/')}>
                Browse pharmacies
              </Button>
            </>
          }
        />
      </>
    );
  }

  return (
    <>
      <AppBar back title={`Cart · ${lines.length} item${lines.length === 1 ? '' : 's'}`} />

      <div className="mx-auto w-full max-w-[900px] px-4 pb-6 xl:px-6">
        {isError ? (
          <ErrorState
            title="We couldn't price your cart"
            body="Your items are still here. This is usually a connection problem."
            action={
              <Button full icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
          />
        ) : isLoading || !cart ? (
          <CartSkeleton />
        ) : (
          <>
            <div className="space-y-3 pt-3.5">
              {cart.groups.map((group) => (
                <PharmacyGroup key={group.pharmacy?.id ?? 'unknown'} group={group} />
              ))}
            </div>

            {cart.delivery_count > 1 ? (
              <p className="mt-3 flex items-start gap-2 rounded-card border border-blue-tint bg-blue-tint-2 px-3.5 py-3 text-[12px] leading-relaxed text-blue-deep">
                <Icon name="truck" size={15} className="mt-px shrink-0" />
                <span>
                  This order ships in <b>{cart.delivery_count} deliveries</b>, one per pharmacy —
                  so there is a delivery fee for each.
                </span>
              </p>
            ) : null}

            <Card className="mt-3">
              <Row label={`Goods · ${lines.length} item${lines.length === 1 ? '' : 's'}`} value={cart.goods_total_display} />
              <Row
                label={`Delivery · ${cart.delivery_count} ${cart.delivery_count === 1 ? 'pharmacy' : 'pharmacies'}`}
                value={cart.delivery_total_display}
              />
              {cart.credit_applied_kobo > 0 ? (
                <Row label="Refund credit" value={`−${cart.credit_applied_display}`} tone="verified" />
              ) : null}

              <div className="my-3 h-px bg-line-2" />

              <div className="flex items-center justify-between">
                <b className="text-[14px]">Total</b>
                <span className="font-display text-[20px] font-bold tracking-[-0.02em]">
                  {cart.grand_total_display}
                </span>
              </div>
            </Card>
          </>
        )}
      </div>

      {cart && !isLoading ? (
        <div className="sticky bottom-0 border-t border-line bg-white px-4 pb-3.5 pt-3 shadow-[0_-4px_16px_rgba(8,19,43,0.06)]">
          <div className="mx-auto max-w-[900px]">
            <Button
              full
              disabled={!cart.is_fulfillable}
              onClick={() => navigate(user ? '/checkout' : '/sign-in', { state: { from: '/checkout' } })}
            >
              {cart.is_fulfillable
                ? `Proceed to checkout · ${cart.grand_total_display}`
                : 'Fix the items above to continue'}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}

function PharmacyGroup({ group }: { group: PricedCartGroup }) {
  return (
    <Card flush>
      <div className="flex items-center gap-2.5 border-b border-line-2 bg-blue-tint-2 px-3.5 py-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-blue-tint font-display text-[11px] font-bold text-blue">
          {group.pharmacy?.initials}
        </span>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-[12.5px]">{group.pharmacy?.name}</b>
          <span className="text-[11px] text-faint">
            Delivery {group.delivery_fee_display}
          </span>
        </div>
        {/* No verified mark — see HomeScreen. */}
      </div>

      <div className="divide-y divide-line-2">
        {group.lines.map((line) => (
          <CartRow key={line.listing_id} line={line} />
        ))}
      </div>

      {group.requires_prescription ? (
        <div className="border-t border-line-2 bg-caution-tint px-3.5 py-3">
          <p className="m-0 flex items-start gap-2 text-[11.5px] leading-relaxed text-caution-ink">
            <Icon name="alert" size={14} className="mt-px shrink-0 text-caution" />
            <span>
              <b>Needs a prescription.</b> Our pharmacist reviews it before this pharmacy dispenses,
              and nothing is charged until they approve.
            </span>
          </p>
        </div>
      ) : null}
    </Card>
  );
}

function CartRow({ line }: { line: PricedCartLine }) {
  return (
    <div className="p-3.5">
      <div className="flex items-start gap-3">
        <PackBox pack={line.pack_art as PackArtKey} className="h-14 w-14 shrink-0" />

        <div className="min-w-0 flex-1">
          {/* The cart is shared by both shops on purpose — one person, one
              order, one delivery. That makes this the one place where a
              veterinary pack and a human one sit side by side with no chrome
              to tell them apart, so the LINE says it. */}
          {line.is_veterinary ? (
            <span
              data-testid="cart-animal-marker"
              className="mb-1 inline-flex items-center rounded-pill border border-blue px-2 py-[2px] font-mono text-[9.5px] uppercase tracking-[0.12em] text-blue"
            >
              For animals only
            </span>
          ) : null}

          <b className="block text-[12.5px] leading-snug">{line.name}</b>
          <div className="mt-0.5 text-[11px] text-faint">
            {line.pack_size}
            {line.batch_no ? ` · ${line.batch_no}` : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-display text-[14px] font-bold tracking-[-0.02em]">
              {line.line_total_display}
            </span>

            <Stepper
              quantity={line.quantity}
              onChange={(next) => cartStore.setQuantity(line.listing_id, next)}
            />
          </div>

          {line.issue_message ? (
            <p
              className={cn(
                'mt-2 flex items-start gap-1.5 text-[11px] font-medium',
                line.issue === 'partial_stock' ? 'text-caution' : 'text-danger',
              )}
            >
              <Icon name="alert" size={12} className="mt-px shrink-0" />
              {line.issue_message}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** §11: 44px touch targets. A quantity stepper is tapped, often in a hurry. */
function Stepper({ quantity, onChange }: { quantity: number; onChange: (next: number) => void }) {
  return (
    <div className="flex items-center overflow-hidden rounded-lg border border-line">
      <button
        type="button"
        aria-label={quantity === 1 ? 'Remove from cart' : 'Reduce quantity'}
        onClick={() => onChange(quantity - 1)}
        className="grid h-11 w-11 place-items-center text-muted hover:bg-paper"
      >
        <Icon name={quantity === 1 ? 'trash' : 'minus'} size={14} strokeWidth={2.4} />
      </button>
      <span className="w-8 text-center font-mono text-[13px] font-bold tabular">{quantity}</span>
      <button
        type="button"
        aria-label="Increase quantity"
        onClick={() => onChange(quantity + 1)}
        className="grid h-11 w-11 place-items-center bg-blue-tint text-blue hover:bg-blue-tint/70"
      >
        <Icon name="plus" size={14} strokeWidth={2.4} />
      </button>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'verified';
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-[11.5px] text-muted">{label}</span>
      <span
        className={cn(
          'font-mono text-[12.5px] tabular',
          tone === 'verified' && 'font-bold text-verified',
        )}
      >
        {value}
      </span>
    </div>
  );
}

function CartSkeleton() {
  return (
    <div className="space-y-3 pt-3.5">
      {Array.from({ length: 2 }).map((_, index) => (
        <Skeleton key={index} className="h-40 w-full" radius="card" />
      ))}
      <Skeleton className="h-28 w-full" radius="card" />
    </div>
  );
}

function EmptyCartArt() {
  return (
    <svg viewBox="0 0 100 100" aria-hidden className="h-full w-full">
      <path
        d="M18 30h10l10 40h38l8-30H33"
        fill="none"
        stroke="var(--color-line)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="43" cy="80" r="5" fill="var(--color-line)" />
      <circle cx="70" cy="80" r="5" fill="var(--color-line)" />
      <circle cx="62" cy="34" r="13" fill="none" stroke="var(--color-blue)" strokeWidth="3.5" />
      <path d="M72 44l9 9" stroke="var(--color-blue)" strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}
